import { request as webxRequest } from '@didi/webx-js'
function isFunction(fn) {
  return typeof fn === 'function'
}

/**
 * service基类
 * @class BaseService
 * @constructor
 *  @param {string} host 请求host
 *  @param {object} interceptors 请求拦截器
 *  @param {object} commonData 公共data,在get请求是会放在params里，post放在data里
 *  @param {object} commonOption 公共option, 如果option里有data优先级高于commonData
 */
class BaseService {
  constructor({ host, interceptors, commonData, commonOption }) {
    this._interceptors = this._mergeInterceptors(interceptors)
    this._commonData = commonData || {}
    this._host = host
    this._commonOption = commonOption || {}
  }

  _mergeInterceptors(interceptors) {
    const INTERCEPTORS = {
      request: request => {
        let { option, url } = request
        if (!/https?:\/\//.test(url)) {
          url = this._host + url
        }
        return Object.assign(request, {
          option,
          url,
        })
      },
      response: response => {
        return response
      },
    }
    return Object.assign(INTERCEPTORS, interceptors)
  }

  set host(val) {
    this._host = val
  }

  get host() {
    return this._host
  }

  _requestCheck({ method, url, option = {} }) {
    if (method.toUpperCase() === 'GET' && option.data) {
      console.warn(`BaseService: 在GET请求使用了 data 参数\nURL: ${url}`)
    }

    if (method.toUpperCase() === 'POST' && option.params) {
      console.warn(`BaseService: 在POST请求使用了 params 参数\nURL: ${url}`)
    }

    if (option.header) {
      const contentType =
        option.header['content-type'] ||
        option.header.contentType ||
        option.header['Content-Type']
      if (
        /application\/json|application\/x-www-form-urlencoded/.test(contentType)
      ) {
        console.warn(
          `BaseService: 请优先使用 emulateJSON 参数代替 header\nURL: ${url}`
        )
      }
    }
  }

  _addCommonData(method, option) {
    switch (method.toUpperCase()) {
      case 'GET':
      case 'DELETE':
        option.params = Object.assign(
          {},
          isFunction(this._commonData)
            ? this._commonData(option)
            : this._commonData,
          option.params
        )
        break
      case 'PUT':
      case 'POST':
        option.data = Object.assign(
          {},
          isFunction(this._commonData)
            ? this._commonData(option)
            : this._commonData,
          option.data
        )
        break
    }
    return option
  }

  /**
   * 通用请求方法
   * @param {string} request.method 请求类型
   * @param {string} request.url 请求url
   * @param {object} request.option 请求参数
   * @returns {Promise<object>} 请求返回
   */
  async request(request) {
    request = isFunction(this._interceptors.request)
      ? this._interceptors.request(request)
      : request
    this._requestCheck(request)
    let { method, url, option = {} } = request

    /** 合并公共参数 */
    option = this._addCommonData(method, option)
    const res = await webxRequest(method, url, {
      ...this._commonOption,
      ...option,
    })
    let data = isFunction(this._interceptors.response)
      ? this._interceptors.response(res)
      : res
    if (data.errno === undefined) {
      data = {
        errno: 0,
        data: data,
      }
    }
    return data
  }

  /**
   * GET请求
   * @param {string} request.url 请求url
   * @param {object} request.option 请求参数
   * @returns {Promise<object>} 请求返回
   */
  get(url, option = {}) {
    return this.request({ method: 'GET', url, option })
  }

  /**
   * POST请求
   * @param {string} url 请求url
   * @param {Object} data 请求体
   * @param {Object} option 请求参数
   * @returns {Promise<object>} 请求返回
   */
  post(url, data, option = {}) {
    option.data = data
    return this.request({ method: 'POST', url, option })
  }
}

export default BaseService
